import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import {DebtSecurity, DebtSecurityWithIssuer, DebtSecurityWithMetrics} from "@/integrations/supabase/debt.models.ts";
import securitiesTransactionService from "@securitiesTransactionService";
import {calculateDaysUntilMaturity} from "@securityUtils";

/**
 * Debt Security Service manages debt security operations
 */
class DebtSecurityService extends BaseService {
    /**
     * Fetches all equity monthly snapshots for a given user.
     * @param {string} userId
     */
    async fetchDebtSecurities(userId: string): Promise<DebtSecurityWithIssuer[]> {
        const { data, error } = await supabase
            .from('debt_securities')
            .select(`*, issuer:bond_issuers(*)`)
            .eq('user_id', userId)
            .order('created_at', { ascending: false });
        if (error) {
            await this.handleError(error);
            throw error;
        }
        return data
    }

    async fetchDebtSecurityById(request: DebtSecurityFetchByIdRequest): Promise<DebtSecurityWithMetrics> {
        const { id, userId } = request;

        const { data: security, error: securityError } = await supabase
            .from('debt_securities')
            .select(`*,issuer:bond_issuers(*)`)
            .eq('id', id)
            .eq('user_id', userId)
            .single();

        if (securityError) {
            await this.handleError(securityError);
            throw securityError;
        }

        const transactions = await securitiesTransactionService.fetchSecurityTransactionsForSecurity({
            securityId: id,
            userId,
            securityType: 'debt'
        });

        const daysUntilMaturity = calculateDaysUntilMaturity(security.maturity_date);

        return {
            ...security,
            daysUntilMaturity,
            transactions: transactions || [],
        }
    }

    /**
     * Creates a debt security for a given user.
     * @param {DebtSecurityCreateRequest} request request to create a debt security
     */
    async createDebtSecurity(request: DebtSecurityCreateRequest): Promise<DebtSecurity> {
        const payload = this.convertRequestToSnakeCase(request);
        const { data, error } = await supabase
            .from('debt_securities')
            .insert({ ...payload, user_id: payload.user_id })
            .select()
            .single();

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data
    }

    async updateDebtSecurity(request: DebtSecurityUpdateRequest): Promise<DebtSecurity> {
        const payload = this.convertRequestToSnakeCase(request);
        const { data, error } = await supabase
            .from('debt_securities')
            .update(payload)
            .eq('id', payload.id)
            .select()
            .single();

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data
    }

    /**
     * Deletes a debt security for a given user.
     * @param {DebtSecurityDeleteRequest} request request to delete a debt security
     */
    async deleteDebtSecurity(request: DebtSecurityDeleteRequest): Promise<void> {
        const { id, userId } = request;
        const { error } = await supabase
            .from('debt_securities')
            .delete()
            .eq('id', id)
            .eq('user_id', userId);

        if (error) {
            await this.handleError(error);
            throw error;
        }
    }

    /**
     * Checks if an ISIN for a debt security already exists.
     * @param {DebtSecurityCheckISINRequest} request request to check if an ISIN for a debt security already exists
     */
    async checkISINExists(request: DebtSecurityCheckISINRequest): Promise<DebtSecurityCheckISINResponse> {
        const { isin, userId } = request;
        if (!isin || isin.length !== 12) {
            return { exists: false, existingSecurityId: null };
        }

        const { data, error } = await supabase
            .from('debt_securities')
            .select('id')
            .eq('user_id', userId)
            .eq('isin', isin)
            .maybeSingle();

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return {
            exists: !!data,
            existingSecurityId: data.id || null,
        };
    }
}

export default new DebtSecurityService();